import {
  Alert,
  Button,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import useApp from "hooks/useApp";
import { Feature } from "models/api";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import DeleteWorkshopsGroupInvitation from "./partials/DeleteWorkshopsGroupInvitation/DeleteWorkshopsGroupInvitation";
import { deleteWorkshopsGroupInvitation } from "framework/workshopsGroups";
import useFetchSection from "hooks/useFetchSection";
import { AttendedListGroupCells } from "framework/workshops";
import AddWorkshopInvitations2 from "./partials/AddWorkshop/AddWorkshopInvitations2";
import { addInvitation, getInvitations } from "framework/workshopInvitations";
import EmptyContent from "components/EmptyContent";

interface Props {
  feature: Feature;
}

const WorkshopGroupDetails: React.FC<Props> = ({ feature }) => {

  const urlParams = useParams();
  let id: string = urlParams[`${feature.singleName}Id`] as string;

  const { t ,i18n} = useTranslation()
    const [openAddInvitation, setOpenAddInvitation] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const { setAlert } = useAlert();
  const [loading2, setLoading2] = useState<boolean>(false);


  const deleteWorkshopsGroupMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteWorkshopsGroupInvitation(code);
    },
  });

  const sendInvitationsMutation = useMutation({
    mutationFn: (createInput: any) => {
      return addInvitation({
        ...createInput,
      });
    },
  });

  const {
    loading,
    data: Table,
    error,
    revalidate
  } = useFetchSection({
    fetchData: () => getInvitations(`?workshopId=${id}`),
    fetchPermission: () => null!,
    section: "Invitation Table",
  });




  const handleOpenAddInvitation = () => {
    setOpenAddInvitation(true);
  };


  const handleSendInvitations = async (selectedEmployees:string) => {
    if (!selectedEmployees)
      return setAlert({
        open: true,
        message: "Please select at least one employee",
        type: "error",
      });
    try {
      setLoading2(true);
      await sendInvitationsMutation.mutateAsync({ workshopGroupId: id, employeeIds: selectedEmployees });
      setAlert({
        open: true,
        message: "Workshop Group invitations sent successfully",
        type: "success",
      });
      revalidate()
      handleClose()
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };


  const handleOpenDelete = (invitation: any) => {
    setSelectedId(invitation.id);
    setOpenDelete(true);
  };

  const handleClose = () => {
    setOpenDelete(false);
    setOpenAddInvitation(false)
    setLoading2(false);
    setSelectedId('');
  };


  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteWorkshopsGroupMutation.mutateAsync(selectedId);
      setAlert({
        open: true,
        message: "Workshop Group Invitation deleted successfully",
        type: "success",
      });
      
      handleClose();
      revalidate()
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };


  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );




  const getAttendedBodyCells = (row: {employeeName:string,employeeEmail:string,departmentName:string}) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "employeeName")
        return {
          id: "employeeName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.employeeName}</Typography>
            </TableCell>
          ),
        };
      if (key === "employeeEmail")
        return {
          id: "employeeEmail",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.employeeEmail}</Typography>
            </TableCell>
          ),
        };

      if (key === "departmentName")
        return {
          id: "departmentName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.departmentName}</Typography>
            </TableCell>
          ),
        };

        if (key === "id")
          return {
            id: "actions",
            element: (
              <TableCell align={"left"} key={i}>
                <Stack
                  direction={"row"}
                  alignContent={"center"}
                  spacing={1}
                  justifyContent={i18n.language === "en" ? "start" : "end"}
                >
                
  
                
                    <Tooltip title={t("T_delete")}>
                      <IconButton
                        size={"small"}
                        sx={{
                          color : "red",
                          "&:hover": "red",
                        }}
                        onClick={() => handleOpenDelete(row)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                </Stack>
              </TableCell>
            ),
          };

      else return <></>;
    });

    return cells;
  };


  return (
    <div style={{padding:10,marginBlock:20}} >
      <div style={{display:'flex',gap:4,alignItems:'center'}}>
   
            <Typography 
              variant="h3"
              color={"primary.main"}
              style={{ paddingBlock: "30px" }}
            >
              {t("T_workshop_invitations_list")}
            </Typography> 
            <Tooltip title="Add Invitation">
              <IconButton
                sx={{
                  // bgcolor: 'primary.main',
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.light",
                    color: "white",
                  },
                  height: 40,
                  width: 40,
                  transition: "0.5s",
                }}
                onClick={handleOpenAddInvitation}
              >
                <Add />
              </IconButton>
            </Tooltip>
      </div>
       
            {Table!.length > 0 && <List
          headCells={AttendedListGroupCells}
          data={Table!}
          getBodyCells={getAttendedBodyCells}
          feature={feature}
          canEdit={false}
        />}
    
         {(!Table || Table.length === 0) && (
            <EmptyContent
              icon={<BlindsClosedIcon />}
              header="No Invitation list Found"
              message=""
            />
          )}

        {openDelete && (
    <DeleteWorkshopsGroupInvitation
      open={openDelete}
      loading={loading2}
      onClose={handleClose}
      onSubmit={handleDelete}
    />

   
  )}

{openAddInvitation && (
      <AddWorkshopInvitations2
      feature={feature}
        open={openAddInvitation}
        loading={loading2}
        id={id}
        onClose={handleClose}
        onSubmit={handleSendInvitations}
      />
    )}
  </div>
  );
};

export default WorkshopGroupDetails;
